import {
  createClient,
  type SupabaseClient,
  type SupabaseClientOptions,
} from '@supabase/supabase-js';
import { Database, Json } from './lib/database.types';

/**
 * Minimal storage adapter contract (compatible with supabase-js).
 */
export type SupabaseStorageAdapter = {
  getItem: (key: string) => Promise<string | null> | string | null;
  setItem: (key: string, value: string) => Promise<void> | void;
  removeItem: (key: string) => Promise<void> | void;
};

export type SupabaseFactoryOptions = {
  url: string;
  key: string;
  auth?: {
    storage?: SupabaseStorageAdapter;
    storageKey?: string;
    autoRefreshToken?: boolean;
    persistSession?: boolean;
    detectSessionInUrl?: boolean;
  };
  options?: Omit<SupabaseClientOptions<keyof Database & string>, 'auth'>;
};

function inferStorageKey(url: string): string {
  // Keep storageKey stable across platforms and never allow it to be undefined,
  // otherwise supabase-js may call storage.getItem(undefined).
  try {
    const host = new URL(url).hostname; // e.g. evmhclcnvkleahufnoqf.supabase.co
    const ref = host.split('.')[0] || 'local';
    return `sb-${ref}-auth-token`;
  } catch {
    return 'sb-auth-token';
  }
}

/**
 * Create a Supabase client with explicit configuration (storage, env, flags).
 * Use this from platform-specific code (web vs mobile) to avoid web-only globals.
 */
export function createSupabaseClient({
  url,
  key,
  auth,
  options,
}: SupabaseFactoryOptions) {
  return createClient<Database, keyof Database & string>(url, key, {
    ...options,
    auth: {
      autoRefreshToken: auth?.autoRefreshToken ?? true,
      persistSession: auth?.persistSession ?? true,
      detectSessionInUrl: auth?.detectSessionInUrl ?? true,
      storage: auth?.storage,
      storageKey: auth?.storageKey ?? inferStorageKey(url),
    },
  });
}

/**
 * Browser storage adapter (localStorage-based) with a safe fallback for SSR/undefined global.
 */
export function createBrowserStorageAdapter():
  | SupabaseStorageAdapter
  | undefined {
  if (typeof globalThis === 'undefined' || !('localStorage' in globalThis)) {
    return undefined;
  }

  const local = (globalThis as unknown as { localStorage?: Storage })
    .localStorage;
  if (!local) return undefined;

  return {
    getItem: (key: string) => local.getItem(key),
    setItem: (key: string, value: string) => local.setItem(key, value),
    removeItem: (key: string) => local.removeItem(key),
  };
}

/**
 * AsyncStorage adapter for React Native (expects an AsyncStorage-like object).
 */
export function createAsyncStorageAdapter(asyncStorage: {
  getItem: (key: string) => Promise<string | null>;
  setItem: (key: string, value: string) => Promise<void>;
  removeItem: (key: string) => Promise<void>;
}): SupabaseStorageAdapter {
  return {
    getItem: (key: string) => asyncStorage.getItem(key),
    setItem: (key: string, value: string) => asyncStorage.setItem(key, value),
    removeItem: (key: string) => asyncStorage.removeItem(key),
  };
}

/**
 * Memory adapter (used when neither browser nor AsyncStorage are available).
 */
export function createMemoryStorageAdapter(): SupabaseStorageAdapter {
  const store = new Map<string, string>();
  return {
    getItem: (key: string) => store.get(key) ?? null,
    setItem: (key: string, value: string) => {
      store.set(key, value);
    },
    removeItem: (key: string) => {
      store.delete(key);
    },
  };
}

/**
 * Base Supabase client type.
 *
 * NOTE: The web singleton lives in `@upcaret/supabase-types/client`.
 * This core module is side-effect free so it can be imported from workers / React Native.
 */
export type SupabaseClientType = SupabaseClient<Database>;
/**
 * Shape produced by `mapEntitlementToSubscription` (see
 * apps/cently/src/contexts/auth-context.tsx) from the `api.entitlement_current`
 * view. Not tied to a table row type since the legacy `api.subscription`
 * table has been removed in favor of `api.billing_subscription` /
 * `api.entitlement_current`.
 */
type SubscriptionType = {
  id: number;
  user_id: string;
  stripe_subscription_id: string;
  status: Database['api']['Enums']['subscription_status_enum'];
  price_id: string;
  cancel_at_period_end: boolean;
  current_period_start: string;
  current_period_end: string;
  created_at: string;
  updated_at: string;
  trial_end: string | null;
  metadata: Json;
  stripe_account: string;
};
type ReceiptType = Database['api']['Tables']['receipt']['Row'];
type ReceiptStoreType = Database['api']['Tables']['receipt_store']['Row'];
type ReceiptItemType = Database['api']['Tables']['receipt_item']['Row'];
type TransactionsType = Database['api']['Tables']['transaction']['Row'];
type PlaidAccountsType = Database['api']['Tables']['plaid_account']['Row'];
type PlaidItemsType = Database['api']['Tables']['plaid_item']['Row'];

export {
  type Database,
  type SubscriptionType,
  type ReceiptType,
  type ReceiptItemType,
  type ReceiptStoreType,
  type TransactionsType,
  type PlaidAccountsType,
  type PlaidItemsType,
};
